import type { User } from '@/shared/api/types';
import type { LucideIcon } from 'lucide-react';
import type { ComponentType, ReactNode } from 'react';
import type { RouteObject } from 'react-router-dom';

import type { Translations } from '@/lang/interfaces/translationTypes';
import type { AuthAdapter } from '@/platform/auth/AuthAdapter';
import type { I18nBundle } from '@/platform/i18n/types';
import type { UseNotificationsResult } from '@/platform/shell/notifications.types';
import type { SliceLike } from '@/platform/store/createAppStore';
import type { AppDispatch } from '@/platform/store/types';

/** Typed pointer to an i18n key; the shell resolves it with `t(key, { ns })`. */
export type LabelRef = { ns: keyof Translations & string; key: string };

export const label = <NS extends keyof Translations & string>(
  ns: NS,
  key: keyof Translations[NS] & string,
): LabelRef => ({ ns, key });

/**
 * Every brand ships one Logo component that renders the mark alone or the full
 * lockup (mark + wordmark). Consumers never place `brand.name` as styled text
 * next to the mark: the brand owns its wordmark font and colour.
 */
export type BrandLogoProps = {
  /** Render only the mark (default). `false` renders the lockup: mark + wordmark. */
  iconOnly?: boolean;
  /**
   * `mono` (default) paints the mark with `currentColor`: for brand-colored
   * surfaces (sidebar chip) or a tinted mark. `color` asks for the brand's
   * full-color mark on a neutral page surface (login, consent). A brand without
   * one keeps `currentColor`; a brand may also fall back to `currentColor` in
   * dark mode when its color mark does not read well there.
   */
  variant?: 'mono' | 'color';
  /** Classes for the `<svg>` mark (size, and the tint used by `mono` marks). */
  className?: string;
  /** Classes for the wordmark text when `iconOnly` is `false` (typically the size). */
  wordmarkClassName?: string;
};

/** What the login page hands to a brand's `LoginPanel`. */
export interface LoginPanelProps {
  /**
   * Language dropdown and theme switch, already wired and styled for a dark
   * or brand-coloured surface. Render them top-right.
   */
  controls: ReactNode;
  /** The brand lockup in `mono`, white with a soft shadow. Render it at the bottom. */
  lockup: ReactNode;
}

export interface AppBrand {
  /** Display name used in the sidebar, login page and `{{brand}}` copy. */
  name: string;
  Logo: ComponentType<BrandLogoProps>;
  /**
   * The wordmark alone, for the sidebar chip (which draws the mark in its own
   * square). Without it the chip shows `name` as plain text.
   */
  Wordmark?: ComponentType<{ className?: string }>;
  termsUrl?: string;
  /** Shown in the login footer, e.g. `V1.0.0`. */
  version?: string;
  /** Sidebar brand link target. Defaults to the landing route. */
  homeTo?: string;
  /**
   * Right half of the login page (`xl` and up). Owns background and content
   * and places the wired `controls` and `lockup`. Defaults to the platform's
   * message panel over the animated brand gradient.
   */
  LoginPanel?: ComponentType<LoginPanelProps>;
}

export interface DashboardRoute {
  /** Relative to `/app`, e.g. `agents/*`. */
  path: string;
  /** Absolute target, e.g. `/app/agents`; also the React key. */
  to: string;
  /** Usually `React.lazy(...)` so each dashboard only ships what it mounts. */
  Component: ComponentType;
}

export interface NavItemContribution {
  /** Must match an `AppDefinition.navSections[].id`. */
  sectionId: string;
  order?: number;
  to: string;
  label: LabelRef;
}

export interface FooterNavContribution {
  order?: number;
  to: string;
  label: LabelRef;
  icon: LucideIcon;
}

export type SettingsGroupId =
  | 'account'
  | 'integrations'
  | 'organization'
  | 'developers';

/**
 * Who a contribution is for. `user` (the default) is the customer dashboard; `internal` is the staff
 * console, mounted only for `type_user: 'internal_user'`; `all` is shared by both (account, appearance).
 * Filtering routes is also the route guard: a route that is not mounted cannot be reached.
 */
export type ModuleAudience = 'user' | 'internal' | 'all';

export interface SettingsSectionContribution {
  /** Anchor id (`?section=<id>`, element id `s-<id>`). */
  id: string;
  /** Defaults to `user`. */
  audience?: ModuleAudience;
  group: SettingsGroupId;
  order: number;
  label: LabelRef;
  icon: LucideIcon;
  Component: ComponentType;
  /**
   * `true` when the component does not render its own `<section id="s-<id>">`
   * (page-like components); the hub wraps it so the anchor exists.
   */
  wrapInSection?: boolean;
}

/**
 * One feature's contribution to a dashboard: routes, navigation, state,
 * translations and settings sections. Manifests are plain `.ts` files that
 * reference icon components, never JSX.
 */
export interface FeatureModule {
  id: string;
  /** Defaults to `user`: customer modules. See `ModuleAudience`. */
  audience?: ModuleAudience;
  routes?: DashboardRoute[];
  /** Nested under `/app/settings/*` (deep configuration pages). */
  settingsRoutes?: DashboardRoute[];
  nav?: NavItemContribution[];
  footerNav?: FooterNavContribution[];
  slices?: SliceLike[];
  /** Slice keys (reducerPath) to persist. Default: none. */
  persist?: string[];
  /** Namespaces owned by the module. */
  i18n?: I18nBundle;
  settingsSections?: SettingsSectionContribution[];
}

export interface NavSectionDefinition {
  id: string;
  title: LabelRef;
  icon: LucideIcon;
  defaultOpen?: boolean;
}

export interface NotificationsSource {
  /**
   * Hook returning the list. `loadList` must be referentially stable
   * (`useCallback`): the header bell calls it from an effect.
   */
  useNotifications: () => UseNotificationsResult;
}

export interface AuthenticatedContext {
  dispatch: AppDispatch;
  user: Partial<User>;
}

/**
 * Everything that makes one dashboard. `apps/<id>/src/app.definition.ts`
 * builds it with `defineApp` and `bootstrap` turns it into a running app.
 */
export interface AppDefinition {
  /** Short id: folder under `apps/`, storage-key prefix and default persist key. */
  id: string;
  brand: AppBrand;
  auth: AuthAdapter;
  /** Runs after any successful login (password, MFA, Google, token). */
  onAuthenticated?: (ctx: AuthenticatedContext) => void;
  /** Core modules first (`...coreModules`), then the product features. */
  modules: FeatureModule[];
  /** Sidebar groups, in order; nav contributions attach to them by id. */
  navSections: NavSectionDefinition[];
  routes: {
    /** Where `/app` and a fresh login land, e.g. `/app/agents`. */
    landing: string;
    /**
     * Where staff land. Must be mounted by an `internal` or `all` module; omit it and staff land on
     * `landing`, which only works if that route is shared.
     */
    internalLanding?: string;
  };
  /** Extra top-level routes outside `/auth` and `/app` (e.g. `/oauth/*`). */
  topLevelRoutes?: RouteObject[];
  notifications?: NotificationsSource;
  /** Defaults to key `<id>.root`, version 3. */
  persist?: { key?: string; version?: number };
  /**
   * App-level translation namespaces (e.g. login panel copy), merged after
   * the core and module bundles.
   */
  i18n?: I18nBundle;
}
